import { base64UrlEncode } from '@buildautomaton/e2ee';
import type { PromptImagePayload } from './clients/acp-client.js';
import type { CliE2eeRuntime } from '../../lib/e2ee/index.js';
import { logDebug, type LogFn } from '../../log.js';

export type SessionAttachmentRef = { attachmentId: string; mimeType: string };

type AttachmentMetaJson = {
  mimeType?: string;
  encrypted?: boolean | string | number;
  k?: string;
  n?: string;
};

function metaSaysEncrypted(meta: AttachmentMetaJson | null): boolean {
  if (!meta) return false;
  const e = meta.encrypted;
  return e === true || e === 'true' || e === 1;
}

/** Best-effort check that decoded bytes look like a raster image (catches wrong E2EE key / ciphertext sent as body). */
function warnIfDecodedImageMagicUnexpected(buf: Buffer, mimeType: string, idShort: string): void {
  const m = mimeType.toLowerCase();
  if (!m.startsWith('image/') || buf.length < 4) return;
  const b0 = buf[0]!;
  const b1 = buf[1]!;
  const b2 = buf[2]!;
  const b3 = buf[3]!;
  let looksOk = false;
  if (m.includes('png') && b0 === 0x89 && b1 === 0x50 && b2 === 0x4e && b3 === 0x47) looksOk = true;
  else if ((m.includes('jpeg') || m.includes('jpg')) && b0 === 0xff && b1 === 0xd8 && b2 === 0xff) looksOk = true;
  else if (m.includes('gif') && b0 === 0x47 && b1 === 0x49 && b2 === 0x46) looksOk = true;
  else if (
    m.includes('webp') &&
    buf.length >= 12 &&
    buf.subarray(0, 4).toString('ascii') === 'RIFF' &&
    buf.subarray(8, 12).toString('ascii') === 'WEBP'
  )
    looksOk = true;
  else if (!/(png|jpe?g|gif|webp)/i.test(m)) looksOk = true;
  if (!looksOk) {
    logDebug(
      `[Agent] Attachment ${idShort} (${mimeType}): decoded bytes do not match common image signatures — wrong E2EE key, corrupt blob, or metadata mismatch. First 12 bytes (hex): ${buf.subarray(0, Math.min(12, buf.length)).toString('hex')}`
    );
  }
}

export async function fetchSessionAttachmentPayloadsForAgent(params: {
  attachments: SessionAttachmentRef[];
  sessionId: string;
  cloudApiBaseUrl: string;
  getCloudAccessToken: () => string;
  e2ee?: CliE2eeRuntime;
  log: LogFn;
}): Promise<{ ok: true; images: PromptImagePayload[] } | { ok: false; error: string }> {
  const { attachments, sessionId, cloudApiBaseUrl, getCloudAccessToken, e2ee, log } = params;
  const token = getCloudAccessToken();
  if (!token) {
    return { ok: false, error: 'Missing cloud access token; cannot download attachments.' };
  }
  const wantedCount = attachments.filter((a) => typeof a.attachmentId === 'string' && a.attachmentId.trim() !== '').length;
  if (wantedCount === 0) {
    return { ok: false, error: 'No valid attachment ids in prompt.' };
  }
  const base = cloudApiBaseUrl.replace(/\/+$/, '');
  const out: PromptImagePayload[] = [];
  for (const a of attachments) {
    const id = typeof a.attachmentId === 'string' ? a.attachmentId.trim() : '';
    if (!id) continue;
    const metaUrl = `${base}/internal/sessions/${encodeURIComponent(sessionId)}/attachments/${encodeURIComponent(id)}/meta`;
    const blobUrl = `${base}/internal/sessions/${encodeURIComponent(sessionId)}/attachments/${encodeURIComponent(id)}/blob`;
    const metaRes = await fetch(metaUrl, { headers: { Authorization: `Bearer ${token}` } });
    if (!metaRes.ok) {
      const t = await metaRes.text().catch(() => '');
      log(`[Agent] Attachment meta fetch failed ${metaRes.status}: ${t.slice(0, 200)}`);
      return { ok: false, error: `Could not load attachment (${id.slice(0, 8)}…): ${metaRes.status}` };
    }
    const meta = (await metaRes.json().catch(() => null)) as AttachmentMetaJson | null;
    const mimeType = typeof meta?.mimeType === 'string' && meta.mimeType.trim() ? meta.mimeType.trim() : a.mimeType;
    const blobRes = await fetch(blobUrl, { headers: { Authorization: `Bearer ${token}` } });
    if (!blobRes.ok) {
      const t = await blobRes.text().catch(() => '');
      log(`[Agent] Attachment blob fetch failed ${blobRes.status}: ${t.slice(0, 200)}`);
      return { ok: false, error: `Could not load attachment data (${id.slice(0, 8)}…): ${blobRes.status}` };
    }
    const buf = Buffer.from(await blobRes.arrayBuffer());
    let imageBytes: Buffer;
    const encrypted = metaSaysEncrypted(meta);
    if (encrypted) {
      if (!e2ee) {
        return { ok: false, error: 'Encrypted attachments require E2EE keys on this bridge.' };
      }
      const k = typeof meta?.k === 'string' ? meta.k : '';
      const n = typeof meta?.n === 'string' ? meta.n : '';
      if (!k || !n) {
        return { ok: false, error: 'Invalid encrypted attachment metadata (missing key id or nonce).' };
      }
      const c = base64UrlEncode(buf);
      imageBytes = e2ee.decryptEnvelopeToBuffer({ k, n, c });
    } else {
      imageBytes = buf;
    }
    warnIfDecodedImageMagicUnexpected(imageBytes, mimeType, id.slice(0, 8));
    logDebug(
      `[Agent] Loaded prompt image ${id.slice(0, 8)}…: ${imageBytes.length} bytes, mime=${mimeType}, ${encrypted ? 'E2EE decrypted' : 'plaintext from storage'}`
    );
    const dataBase64 = imageBytes.toString('base64');
    out.push({ mimeType, dataBase64 });
  }
  if (out.length !== wantedCount) {
    return {
      ok: false,
      error: `Expected ${wantedCount} image attachment(s) but only loaded ${out.length} (check attachment ids and empty rows).`,
    };
  }
  return { ok: true, images: out };
}
